import { useCallback, useRef } from 'react'
import { deriveLevelState, formatAmount, levelPopoverEventKey } from './levelHelpers'
import LevelTrack from './LevelTrack'
import LevelBadges from './LevelBadges'
import type { LevelPopoverOpenHandler, LevelTrackerProps } from './levelTrackerTypes'

/**
 * Level progress tracker (Zoomed in Milestones, CAX-3900): zooms in on the
 * current level bracket, with a badge legend and level popovers.
 *
 * Presentational: levels arrive already built (`buildLevels`) and all copy comes
 * from `labels`. Parallel with Tributary's level-tracker.tsx (contribution
 * progress parity, Rule 3): same component breakdown and props, Prologue styling.
 */
export default function LevelTracker({
  received,
  levels,
  contributors,
  daysRemaining,
  labels,
  onLevelPopoverOpen,
}: LevelTrackerProps) {
  const sentEvents = useRef(new Set<string>())

  const handlePopoverOpen = useCallback<LevelPopoverOpenHandler>(
    (level, trigger, method) => {
      const event = { level, trigger, method, unlocked: received >= (levels[level - 1]?.amount ?? Infinity) }
      const key = levelPopoverEventKey(event)
      if (sentEvents.current.has(key)) return
      sentEvents.current.add(key)
      onLevelPopoverOpen?.(event)
    },
    [levels, received, onLevelPopoverOpen],
  )

  const state = deriveLevelState(received, levels)
  if (!state) return null

  const days = labels.daysRemaining(daysRemaining)

  return (
    <div id='level-tracker' className='flex flex-col  gap-y-1 sm:gap-y-6 font-sans'>
      <div className='flex flex-wrap items-baseline justify-between mb-7 gap-x-4 gap-y-2 max-md:whitespace-nowrap sm:gap-x-6'>
        <div className='flex items-baseline gap-1 sm:gap-2'>
          <span className='text-cta font-serif text-2xl font-bold sm:text-3xl md:text-4xl'>
            {formatAmount(received)}
          </span>
          <span className='text-secondary-txt text-xs text-base sm:text-sm sm:font-semibold'>{labels.received}</span>
        </div>
        {state.done ? (
          <div className='text-secondary-txt text-sm text-base'>{labels.allLevelsUnlocked}</div>
        ) : (
          <div className='flex items-baseline gap-1 sm:gap-2'>
            <span className='text-cta font-serif text-2xl font-bold sm:text-3xl md:text-4xl'>
              {formatAmount(state.remaining)}
            </span>
            <span className='text-secondary-txt text-xs text-base sm:text-sm sm:font-semibold'>
              {state.isFinal ? labels.toGoal : labels.toNextLevel}
            </span>
          </div>
        )}
      </div>

      <LevelTrack state={state} levels={levels} received={received} labels={labels} onPopoverOpen={handlePopoverOpen} />

      <div className='flex flex-col gap-5 pt-1 md:flex-row md:items-end md:justify-between md:gap-8'>
        <LevelBadges levels={levels} earned={state.earned} labels={labels} onPopoverOpen={handlePopoverOpen} />

        <div className='flex flex-row justify-between mt-3 gap-1 md:flex-row md:items-baseline md:gap-7'>
          <div className='flex items-baseline gap-1.5'>
            <span className='text-cta font-serif text-2xl font-bold md:text-3xl'>{contributors}</span>
            <span className='text-secondary-txt text-xs sm:font-semibold md:text-base'>
              {labels.contributors(contributors)}
            </span>
          </div>
          <div className='flex items-baseline gap-1.5'>
            {days.value && <span className='text-cta font-serif text-2xl font-bold md:text-3xl'>{days.value}</span>}
            <span className='text-secondary-txt text-xs  sm:font-semibold md:text-base'>{days.label}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
